import { useRef, useState } from 'react';
import { FolderIcon, ToolboxIcon } from '@phosphor-icons/react';
import { Badge } from '~/components/ui/badge';
import { Tip } from '~/components/ui/tip';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { ContainerPicker, type ContainerPickerOption } from '~/features/work/container-picker';
import type { ChatScopeEntity } from '~/hooks/queries/use-chat-scope';

export interface ChatScopeChipProps {
  /** The chat's current Work-container scope, or `null` when it isn't filed
   *  under one yet. Same shape `inbox-chat-modal.tsx`'s `effectiveScope`
   *  already computes (`initialScope` or `useChatScope`'s read-back). */
  scope: ChatScopeEntity | null;
  /**
   * FRONT-1738 BLOCKER fix: whether this conversation has at least one
   * message in it (`inbox-chat-modal.tsx`'s own `hasMessages`, the exact
   * signal its resolve buttons — Save to Graph / Save as document / Update
   * document — already gate on). The chat's `chat_sessions` row is only
   * OWNED by (org, user) from the first user message (`setSessionOwner`,
   * `session-registry-store.ts`) — before that, `PUT .../scope`'s ownership
   * check matches no row and 404s. A freshly-minted "Ask Eddy" chat's
   * session id (`web:<uuid>`) is generated CLIENT-SIDE the instant the modal
   * mounts, well before any message exists, so the id being a non-empty
   * string is NOT a safe proxy for "there is a row to write to" — hence a
   * dedicated boolean rather than inferring this from the session-id prop.
   *
   * Gates the move icon button in the filed state — the "About: <name>"
   * marker itself must keep rendering immediately from a fresh
   * `initialScope`, on turn zero, before any message is ever sent. Do not
   * widen this to gate the whole component. (FRONT-1813: the unfiled state
   * renders nothing at all now, so this prop no longer has an unfiled
   * action control to gate — see this component's own docblock.)
   */
  hasMessages: boolean;
  /** Called with the container id on click of the "About: <name>" button —
   *  the modal navigates to the container's detail page and closes itself. */
  onOpen: (entityId: string) => void;
  /** File (or move) this chat under an existing project, picked from
   *  `ContainerPicker`. */
  onSelect: (target: ContainerPickerOption) => void;
  /** Clear this chat's scope entirely (filed state's picker only). */
  onClear: () => void;
}

/**
 * FRONT-1191: the "About: <name>" marker shown directly above the composer
 * when this chat is scoped to a Work container (a project) — set the
 * instant a fresh scoped chat is minted from the board's/detail page's "Ask
 * Eddy about this" action, or read back via `useChatScope` when an
 * already-scoped chat is reopened later.
 *
 * FRONT-1738 made the marker a CONTROL too, but only in the filed state:
 * the original "About: <name>" button is unchanged — same testid, same
 * navigate-and-close click, rendered regardless of `hasMessages` — with a
 * small icon button beside it (shown only once `hasMessages`) that reopens
 * `ContainerPicker` to move the chat to a different container or clear its
 * scope entirely (`excludeId` keeps the picker from offering a no-op move
 * back to the same container, and `onSelectNew` is omitted — moving a chat
 * must resolve to an EXISTING active container, never mint one).
 *
 * FRONT-1813 removed the unfiled state's own "Add to project" chip — that
 * control duplicated a capability Eddy already has (`work_chat_move`,
 * `src/work/chat-scope-tool.ts`), so a chat with no scope yet now renders
 * NOTHING from this component. Filing a fresh, unscoped chat under a
 * project happens either by starting the chat from the project itself
 * ("Project chat about ⟨name⟩" on the Work board/detail page,
 * `ask-eddy-button.tsx` — renamed from "Ask Eddy" by FRONT-1808) or by
 * asking Eddy in the chat to file it — never by hand from this control
 * anymore. The filed state's move/remove control is unaffected by that
 * change.
 *
 * The move trigger deliberately does NOT use the `Tip` convenience wrapper
 * — same reasoning as `note-row.tsx`'s Move control: `Tip` renders no DOM
 * of its own for `ContainerPicker`'s inner `PopoverTrigger asChild` to land
 * its cloned props on, so `<PopoverTrigger asChild><Tip>` silently drops the
 * popover's click handling (button still focuses, picker never opens).
 * Composing the raw `Tooltip`/`TooltipTrigger` primitives instead —
 * `Tooltip` outermost, `TooltipTrigger asChild` wrapping the picker's own
 * trigger — carries the popover's props all the way down to the real
 * `<button>`. Uses the design-system tokens throughout (never a raw
 * Tailwind color scale) and a real `aria-label` on the icon-only move
 * button (a tooltip is not an accessible name).
 *
 * "project" in every user-facing string below ("Remove from project", "Move
 * to another project") is the real, literal kind now — `routine` was
 * retired as a Work container kind by FRONT-2378.
 *
 * FRONT-1738 fix: closing the move picker used to strand the dialog's own
 * Escape handling — the browser check would time out waiting for the chat
 * dialog to close, with a "Move to another project" tooltip visibly
 * floating over the folder button. Two INDEPENDENT mechanisms compounded
 * into that one symptom; both had to be fixed for a single Escape to
 * reliably close the dialog, and neither was found by reasoning about the
 * code alone — both came from comparing live diagnostic runs.
 *
 * Mechanism 1 — a phantom tooltip. `PopoverContent`'s default
 * `onCloseAutoFocus` returns focus to its trigger, which here is a
 * `TooltipTrigger`-wrapped button — and a Radix `Tooltip` opens on FOCUS,
 * not only hover, so the returned focus re-opened "Move to another
 * project" and registered a fresh `DismissableLayer` on top of the
 * dialog's own. `useEscapeKeydown` listens at `document`, in the CAPTURE
 * phase; the next physical Escape hit that layer first, which calls
 * `event.preventDefault()` (never `stopPropagation()`) before closing just
 * the tooltip — and `inbox-chat-modal.tsx`'s own Escape handler is a
 * bubble-phase `window` listener that already bails on `e.defaultPrevented`
 * (FRONT-1579, for the same reason: don't let one Escape close two stacked
 * layers at once). So the first Escape silently closed the phantom tooltip
 * and nothing else; only a second Escape reached the dialog. (First attempt
 * redirected the returned focus to the "About: <name>" marker instead —
 * WRONG: that marker is itself a `Tip`-wrapped tooltip trigger, so focusing
 * it programmatically just re-opened a DIFFERENT tooltip and reproduced the
 * identical bug one button over, caught by a live run showing
 * `data-state="instant-open"` on the marker itself.)
 *
 * The actual fix for mechanism 1 leaves Radix's default focus-return alone
 * (focus keeps landing exactly where it always did) and SUPPRESSES the one
 * resulting tooltip-open that follows a picker close, on the move button's
 * own tooltip only. `suppressNextTooltipOpenRef` is armed the instant the
 * picker reports closing, for every reason (`open` going to `false` covers
 * a selection, a clear, AND a plain dismiss — Radix funnels all three
 * through the same `onOpenChange`), and consumed by the very next open
 * attempt on the move tooltip; a genuine hover or a genuine keyboard Tab
 * into the button arms nothing, so both keep opening the tooltip exactly as
 * before.
 *
 * Mechanism 2 — the popover's own lingering layer. With mechanism 1 fixed,
 * the SAME outward symptom still reproduced (dialog needs two Escapes),
 * with the tooltip fully suppressed and nothing in the accessibility tree.
 * Found by comparing two live runs of the identical fixed code: one with an
 * extra half-second pause before pressing Escape (passed, one Escape), one
 * without (failed, needed two) — the only variable was how soon Escape
 * followed the click. Radix's `Presence` keeps a CLOSING `PopoverContent`
 * — its `DismissableLayer` and live Escape handling included — mounted for
 * as long as its CSS exit animation plays (~100ms, this design system's
 * `duration-100`); it only tears that layer down synchronously, immediately,
 * when it detects no animation running. An Escape pressed inside that
 * ~100ms tail (trivial for an automated click-then-Escape sequence, and not
 * impossible for a fast human) finds the popover's own layer still
 * registered and still the highest one, which consumes it the exact same
 * `preventDefault`-without-`stopPropagation` way the tooltip did — even
 * though the popover was already told to close, moments earlier, by the
 * very same click. `ContainerPicker`'s `instantClose` prop (see its own
 * docblock — an inline `animation: 'none'` style does NOT work here,
 * Radix's own `Content` silently overwrites it) gates `PopoverContent` on
 * `isOpen` in plain React instead, bypassing Presence's animated-exit
 * deferral entirely, so nothing is left mounted to catch a fast Escape.
 * `note-row.tsx`'s Move control does not pass it and keeps its animated
 * close, unaffected — confirmed by running its own browser check
 * (E2E-WORK-09) green, unmodified, after this fix landed.
 */
export function ChatScopeChip({ scope, hasMessages, onOpen, onSelect, onClear }: ChatScopeChipProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [moveTooltipOpen, setMoveTooltipOpen] = useState(false);
  const suppressNextTooltipOpenRef = useRef(false);

  // FRONT-1813: an unfiled chat renders nothing — see this component's own
  // docblock for where filing moved to (the project's "Project chat"
  // button, or Eddy's own `work_chat_move` tool).
  if (!scope) return null;

  return (
    <div className="flex items-center gap-1">
      <Tip label="Open this project">
        <button
          type="button"
          data-testid="chat-scope-chip"
          onClick={() => onOpen(scope.id)}
          className="inline-flex max-w-[420px] shrink-0 items-center gap-1.5 self-start rounded-md border border-border bg-secondary px-2.5 py-1.5 text-xs font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
        >
          <ToolboxIcon size={13} className="shrink-0" />
          <span className="min-w-0 truncate">About: {scope.name || 'Untitled'}</span>
          {scope.archived && (
            <Badge variant="secondary" className="shrink-0 py-0">
              Inactive
            </Badge>
          )}
        </button>
      </Tip>
      {hasMessages && (
        <Tooltip
          open={moveTooltipOpen}
          onOpenChange={(open) => {
            // FRONT-1738: swallow exactly one open attempt right after the
            // picker closes — see the docblock above (mechanism 1).
            if (open && suppressNextTooltipOpenRef.current) {
              suppressNextTooltipOpenRef.current = false;
              return;
            }
            setMoveTooltipOpen(open);
          }}
        >
          <ContainerPicker
            excludeId={scope.id}
            onSelectExisting={onSelect}
            onClear={onClear}
            clearLabel="Remove from project"
            instantClose
            open={pickerOpen}
            onOpenChange={(open) => {
              if (!open) suppressNextTooltipOpenRef.current = true;
              setPickerOpen(open);
            }}
            trigger={
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid="chat-scope-move"
                  aria-label="Move to another project"
                  className="inline-flex shrink-0 items-center justify-center rounded-md border border-border bg-transparent p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-secondary-foreground"
                >
                  <FolderIcon size={13} />
                </button>
              </TooltipTrigger>
            }
          />
          <TooltipContent side="top">Move to another project</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
